import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import z from 'zod'
import { Brand } from '../../shared/enums'

extendZodWithOpenApi(z)

export const ProductType = {
  MEAL: 'meal',
  TREAT: 'treat',
  COLLATERAL: 'collateral', // (ice pack, insulation)
  SUPPLEMENT: 'supplement',
  TOY: 'toy',
  BUNDLE: 'bundle',
} as const
export type ProductType = typeof ProductType[keyof typeof ProductType]

export const ProductStatus = {
  ACTIVE: 'active',
  OUT_OF_STOCK: 'out_of_stock',
  DISCONTINUING: 'discontinuing',
  DISCONTINUED: 'discontinued',
  ARCHIVED: 'archived',
} as const
export type ProductStatus = typeof ProductStatus[keyof typeof ProductStatus]

export const ProductImageType = {
  ICON: 'icon',
  POUCH: 'pouch',
  BOWL: 'bowl',
  BOWL_ICON: 'bowl_icon',
  BOWL_ICON_POUCH: 'bowl_icon_pouch',
  TREAT: 'treat',
  ICE_PACK_BOTTOM: 'ice_pack_bottom',
  ICE_PACK_TOP: 'ice_pack_top',
  EXTRA: 'extra',
} as const
export type ProductImageType = typeof ProductImageType[keyof typeof ProductImageType]

export const RecommendationType = {
  NOT_RECOMMENDED: 'not_recommended',
  NEUTRAL: 'neutral',
  RECOMMENDED: 'recommended',
  BLOCKED: 'blocked',
} as const
export type RecommendationType = typeof RecommendationType[keyof typeof RecommendationType]

export const ProductBarcodeSchema = z.object({
  id: z.string().openapi({ description: 'Barcode unique identifier' }),
  barcodeValue: z.string().openapi({ description: 'Value of the barcode' }),
  barcodeType: z.string().nullable().openapi({ description: 'Type of barcode (if any)' }),
  isActive: z.boolean().openapi({ description: 'Indicates if the barcode is active' }),
  removedAt: z.date().nullable().openapi({ description: 'Timestamp when the barcode was removed' }),
})

export type ProductBarcodeSchema = z.infer<typeof ProductBarcodeSchema>

export const ProductDimensionSchema = z.object({
  id: z.string().uuid().openapi({ description: 'Dimension unique identifier' }),
  weightGrams: z.number().nullable().openapi({ description: 'Product weight in grams' }),
  volumetricWeightGrams: z.number().nullable().openapi({ description: 'Volumetric weight in grams' }),
  dimensionHeightMm: z.number().nullable().openapi({ description: 'Height in millimeters' }),
  dimensionWidthMm: z.number().nullable().openapi({ description: 'Width in millimeters' }),
  dimensionDepthMm: z.number().nullable().openapi({ description: 'Depth in millimeters' }),
  pouchLetter: z.string().nullable().openapi({ description: 'Pouch letter code' }),
})

export type ProductDimensionSchema = z.infer<typeof ProductDimensionSchema>

export const ProductImageSchema = z.object({
  id: z.string().uuid().openapi({ description: 'Image unique identifier' }),
  imageUrl: z.string().openapi({ description: 'URL of the image' }),
  imageType: z.preprocess(
    val => typeof val === 'string' ? val.toLowerCase() : val,
    z.nativeEnum(ProductImageType),
  ).nullable().openapi({ description: 'Image type' }),
  removedAt: z.date().nullable().openapi({ description: 'Timestamp when image was removed' }),
})

export type ProductImageSchema = z.infer<typeof ProductImageSchema>

export const ProductMetadataSchema = z.object({
  id: z.string().uuid().openapi({ description: 'Metadata unique identifier' }),
  metaKey: z.string().openapi({ description: 'Key of the metadata' }),
  metaValue: z.any().nullable().openapi({ description: 'Value associated with the key' }),
})

export type ProductMetadataSchema = z.infer<typeof ProductMetadataSchema>

export const ProductVariantSchema = z.object({
  id: z.string().uuid().openapi({ description: 'Variant unique identifier' }),
  sku: z.string().openapi({ description: 'Stock keeping unit' }),
  variantName: z.string().openapi({ description: 'Name of the variant' }),
  variantDescription: z.string().nullable().optional().openapi({ description: 'Description of the variant' }),
  basePrice: z.number().int().openapi({ description: 'Base price of the variant' }),
  subscriptionPrice: z.number().int().nullable().optional().openapi({ description: 'Subscription price of the variant. (Only extra item have subscription price)' }),
  status: z.nativeEnum(ProductStatus).openapi({ description: 'Status of the variant' }),
  isVisible: z.boolean().openapi({ description: 'Visibility status of the variant' }),
  createdAt: z.string().openapi({ description: 'Creation timestamp' }),
  updatedAt: z.string().openapi({ description: 'Last updated timestamp' }),
  dimension: ProductDimensionSchema.nullable().openapi({ description: 'Dimension information' }),
  barcode: ProductBarcodeSchema.nullable().openapi({ description: 'Barcode information' }),
  images: z.array(ProductImageSchema).openapi({ description: 'List of images' }),
  metadata: z.array(ProductMetadataSchema).openapi({ description: 'List of metadata entries' }),
})

export type ProductVariantSchema = z.infer<typeof ProductVariantSchema>

export const ProductSchema = z.object({
  id: z.string().uuid().openapi({ description: 'Unique product identifier' }),
  productCode: z.string().openapi({ description: 'Product code' }),
  name: z.string().openapi({ description: 'Product name' }),
  brand: z.nativeEnum(Brand).openapi({ description: 'Product brand (lyka or tyga)' }),
  type: z.nativeEnum(ProductType).openapi({ description: 'Product type, e.g., MEAL' }),
  status: z.nativeEnum(ProductStatus).openapi({ description: 'Product status, e.g., ACTIVE' }),
  isVisible: z.boolean().openapi({ description: 'Visibility status of the product' }),
  createdAt: z.string().openapi({ description: 'Creation date of the product' }),
  updatedAt: z.string().openapi({ description: 'Last update date of the product' }),
  images: z.array(ProductImageSchema).openapi({ description: 'Product images' }),
  metadata: z.array(ProductMetadataSchema).openapi({ description: 'Product metadata' }),
  variants: z.array(ProductVariantSchema).openapi({ description: 'Available product variants' }),
})

export type ProductSchema = z.infer<typeof ProductSchema>

/** Child variant composition for a bundle parent variant (from product_bundle). */
export const BundleChildProductSchema = z.object({
  id: z.string().uuid().openapi({ description: 'Child product identifier' }),
  productCode: z.string().openapi({ description: 'Child product code' }),
  name: z.string().openapi({ description: 'Child product name' }),
  brand: z.nativeEnum(Brand).openapi({ description: 'Child product brand' }),
  type: z.nativeEnum(ProductType).openapi({ description: 'Child product type' }),
})

export type BundleChildProductSchema = z.infer<typeof BundleChildProductSchema>

export const BundleChildSchema = z.object({
  quantity: z.number().int().positive().openapi({ description: 'Units of this child variant per parent bundle' }),
  variant: ProductVariantSchema.openapi({ description: 'Child product variant' }),
  product: BundleChildProductSchema.openapi({ description: 'Parent catalog product for the child variant' }),
})

export type BundleChildSchema = z.infer<typeof BundleChildSchema>

export const MealRecommendationInputSchema = z.object({
  brand: z.nativeEnum(Brand).default(Brand.LYKA),
  allergens: z.string().array(),
  illnesses: z.string().array(),
})

export type MealRecommendationInputSchema = z.infer<typeof MealRecommendationInputSchema>

export const MealRecommendationSchema = z.object({
  recommendationType: z.nativeEnum(RecommendationType),
  allergens: z.string().array(),
  illnesses: z.string().array(),
})

export type MealRecommendationSchema = z.infer<typeof MealRecommendationSchema>

export const RecommendedMealSchema = z.object({
  product: ProductSchema,
  recommendation: MealRecommendationSchema,
})

export type RecommendedMealSchema = z.infer<typeof RecommendedMealSchema>
